import React, { useState } from 'react';
import { Coffee, Copy, Trash2, RefreshCw } from 'lucide-react';

const HipsumGeneratorTool = () => {
  const [style, setStyle] = useState('hipster'); // hipster, bacon, corporate, pirate, zombie
  const [paragraphs, setParagraphs] = useState(3);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const generators = {
    hipster: {
      name: 'Hipster Ipsum',
      words: [
        'artisan', 'vinyl', 'sustainable', 'craft beer', 'organic', 'etsy', 'aesthetic', 'fixie',
        'brooklyn', 'literally', 'gastropub', 'quinoa', 'kombucha', 'sriracha', 'typewriter',
        'vegan', 'tofu', 'gluten-free', 'cold-pressed', 'kale', 'authentic', 'beard', 'flannel',
        'PBR&B', 'YOLO', 'chambray', 'raw denim', 'selfies', 'meditation', 'mumblecore',
        'skateboard', 'pork belly', 'banjo', 'messenger bag', 'tote bag', 'pour-over', 'pitchfork',
        'farm-to-table', 'single-origin', 'ethical', 'fair trade', 'mindfulness', 'succulents'
      ]
    },
    bacon: {
      name: 'Bacon Ipsum',
      words: [
        'bacon', 'ham', 'pork', 'beef', 'chicken', 'sausage', 'steak', 'ribeye', 'sirloin',
        'brisket', 'chuck', 'tenderloin', 'filet mignon', 'ground round', 'short ribs', 'tri-tip',
        'flank', 'shank', 'corned beef', 'pastrami', 'prosciutto', 'pancetta', 'salami', 'pepperoni',
        'kielbasa', 'andouille', 'bratwurst', 'chorizo', 'frankfurter', 'hot dog', 'meatball',
        'drumstick', 'wing', 'thigh', 'breast', 'turkey', 'jerky', 'strip steak', 'porchetta'
      ]
    },
    corporate: {
      name: 'Corporate Ipsum',
      words: [
        'synergy', 'leverage', 'paradigm', 'scalable', 'proactive', 'bandwidth', 'streamline',
        'optimize', 'stakeholder', 'deliverable', 'circle back', 'touch base', 'deep dive',
        'action items', 'low-hanging fruit', 'move the needle', 'best practices', 'core competency',
        'value-add', 'win-win', 'thought leadership', 'disrupt', 'innovate', 'pivot', 'ecosystem',
        'holistic', 'empower', 'facilitate', 'incentivize', 'robust', 'seamless', 'cutting-edge',
        'game-changer', 'mission-critical', 'next-gen', 'bleeding edge', 'vertical', 'horizontal'
      ]
    },
    pirate: {
      name: 'Pirate Ipsum',
      words: [
        'arr', 'matey', 'ahoy', 'avast', 'shiver me timbers', 'blimey', 'scallywag', 'buccaneer',
        'landlubber', 'seadog', 'plank', 'treasure', 'doubloons', 'galleon', 'jolly roger',
        'cutlass', 'grog', 'rum', 'parrot', 'eye patch', 'peg leg', 'hook', 'booty', 'plunder',
        'pillage', 'swab the deck', 'walk the plank', 'Davy Jones', 'kraken', 'captain', 'crew',
        'first mate', 'anchor', 'cannon', 'port', 'starboard', 'stern', 'bow', 'mast', 'sail'
      ]
    },
    zombie: {
      name: 'Zombie Ipsum',
      words: [
        'brains', 'undead', 'zombie', 'apocalypse', 'shamble', 'groan', 'moan', 'lurch',
        'infection', 'outbreak', 'survivor', 'horde', 'swarm', 'flesh', 'rotting', 'decay',
        'grave', 'cemetery', 'tombstone', 'corpse', 'reanimated', 'virus', 'plague', 'pandemic',
        'headshot', 'barricade', 'bunker', 'supplies', 'weapons', 'fortify', 'evacuate', 'quarantine',
        'walking dead', 'bite', 'scratch', 'infection', 'turn', 'hunt', 'devour', 'consume'
      ]
    }
  };

  const generateText = () => {
    const gen = generators[style];
    const words = gen.words;
    let result = [];

    for (let p = 0; p < paragraphs; p++) {
      const sentenceCount = Math.floor(Math.random() * 4) + 3; // 3-6 sentences
      let paragraph = [];

      for (let s = 0; s < sentenceCount; s++) {
        const wordCount = Math.floor(Math.random() * 12) + 8; // 8-19 words
        let sentence = [];

        for (let w = 0; w < wordCount; w++) {
          const word = words[Math.floor(Math.random() * words.length)];
          sentence.push(w === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word);
        }

        paragraph.push(sentence.join(' ') + '.');
      }

      result.push(paragraph.join(' '));
    }

    setOutput(result.join('\n\n'));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Style</label>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(generators).map(([key, gen]) => (
              <option key={key} value={key}>{gen.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Paragraphs</label>
          <input
            type="number"
            value={paragraphs}
            onChange={(e) => setParagraphs(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
            min="1"
            max="10"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateText}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Coffee size={18} />
          Generate Text
        </button>
        <button
          onClick={generateText}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
          title="Regenerate"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Generated Text</label>
          <textarea
            value={output}
            readOnly
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white resize-none"
          />
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Text'}
        </button>
        <button
          onClick={() => setOutput('')}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Available Styles</h3>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-gray-800 rounded">
            <span className="text-blue-400 font-medium">Hipster Ipsum</span>
            <p className="text-gray-500 mt-1">Artisan, vintage, craft-inspired text</p>
          </div>
          <div className="p-3 bg-gray-800 rounded">
            <span className="text-red-400 font-medium">Bacon Ipsum</span>
            <p className="text-gray-500 mt-1">Meaty, delicious placeholder text</p>
          </div>
          <div className="p-3 bg-gray-800 rounded">
            <span className="text-green-400 font-medium">Corporate Ipsum</span>
            <p className="text-gray-500 mt-1">Business buzzwords and jargon</p>
          </div>
          <div className="p-3 bg-gray-800 rounded">
            <span className="text-yellow-400 font-medium">Pirate Ipsum</span>
            <p className="text-gray-500 mt-1">Arr! Nautical pirate speak</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HipsumGeneratorTool;
